import { BoxProps } from 'UIKit/Box/types'
import useAuthenticationModal from 'hooks/useAuthenticationModal'
import useKeydownEventListener from 'hooks/useKeydownEventListener'
import Column from 'layout/Components/Column'
import React, { useCallback, useEffect } from 'react'
import { Trans } from 'react-i18next'
import { useAuth } from 'state/auth/hooks'
import { useTokenSelected } from 'state/session/hooks'
import styled from 'styled-components'
import { InHouseGameButton, PlinkoAnimationBounce } from 'views/InhouseGame/components/styled'

const PlinkoAutoBetAction: React.FC<{
  isAuto: boolean
  onAutoBet: (runningStatus: boolean) => void
  disabled?: boolean
}> = ({ isAuto, onAutoBet, disabled }) => {
  const playToken = useTokenSelected()

  useEffect(() => {
    return () => {
      onAutoBet(false)
    }
  }, [playToken])

  return isAuto ? (
    <StyledButton
      disabled={disabled}
      onClick={() => {
        onAutoBet(false)
      }}
    >
      <Trans>Stop AutoBet</Trans>

      <PlinkoAnimationBounce ml="8px" />
    </StyledButton>
  ) : (
    <StyledButton
      disabled={disabled}
      onClick={() => {
        onAutoBet(true)
      }}
    >
      <Trans>Start AutoBet</Trans>
    </StyledButton>
  )
}

const PlinkoBetAction: React.FC<
  {
    isAutoBetRunning?: boolean
    mode: 'singleBet' | 'autoBet'
    disabled: boolean
    onBet: () => void
    onAutoBet: (runningStatus: boolean, onCompleted?: () => void) => void
    hotkeyEnable?: boolean
  } & BoxProps
> = ({ mode, disabled, isAutoBetRunning, hotkeyEnable, onAutoBet, onBet, ...props }) => {
  const { hasSession } = useAuth()
  const [onPresentLoginModal] = useAuthenticationModal()

  const toggleAutoBet = (runningStatus: boolean) => {
    onAutoBet(runningStatus)
  }

  const eventHandler = useCallback(
    (event) => {
      if (event && event?.key === ' ') {
        event.preventDefault()

        if (!hotkeyEnable || disabled) return null
        if (mode === 'autoBet') {
          toggleAutoBet(!isAutoBetRunning)
        } else {
          onBet()
        }
      }
    },
    [hotkeyEnable, disabled, isAutoBetRunning, mode, onBet, toggleAutoBet],
  )
  useKeydownEventListener(eventHandler)

  return (
    <Column {...props}>
      {hasSession ? (
        mode === 'autoBet' ? (
          <PlinkoAutoBetAction
            disabled={disabled}
            isAuto={isAutoBetRunning}
            onAutoBet={(runningStatus) => {
              onAutoBet(runningStatus)
            }}
          />
        ) : (
          <StyledButton disabled={disabled} onClick={onBet}>
            <Trans>Bet</Trans>
          </StyledButton>
        )
      ) : (
        <StyledButton onClick={onPresentLoginModal}>
          <Trans>Login and Play</Trans>
        </StyledButton>
      )}
    </Column>
  )
}

const StyledButton = styled(InHouseGameButton)`
  min-height: 42px !important;
  max-height: 42px !important;
`

export default PlinkoBetAction
